import * as buttons from 'smg/buttons';
import { ContrastRatioHex } from 'smg/color';
import { AddCartItemParamsUpload, AddCartItems } from 'smg/customer-api/v2/carts';
import { GetPrice } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { SpellCheck } from 'smg/customer-api/spell-check';
import {
    CustomizerEngine,
    ImageLayer,
} from 'smg/customizer';
import { ShowError } from 'smg/errors';
import * as html from 'smg/html';
import { Mobile } from 'smg/mobile';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { ColorState } from '../../custom-awareness-ribbon-pins/color-state';
import { defaultFontSizes, PresetManager, presets } from '../../custom-awareness-ribbon-pins/preset-manager';
import { RibbonTextLayer } from '../../custom-awareness-ribbon-pins/ribbon-text-layer';
import { TextCustomizerUI } from '../../custom-awareness-ribbon-pins/text-customizer-ui';
import { DEFAULT_FONT_FAMILY } from '../../product-customizer/fonts';
import { Relref } from '../../hugo';
import { ConfirmSpellCheck } from '../../spell-check-modal';

// Store original SVG content for color manipulation
const svgCache = new Map<string, string>();

// Shift a hex color lighter or darker for derivative colors
function shiftColor(hexColor: string, double: boolean = false): string {
    const color = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
    if (color.length !== 6) {
        return hexColor; // Invalid format
    }
    let shiftAmt = 48;
    let shiftThreshold = 2 * shiftAmt;
    if (double) {
        shiftAmt *= 2;
        shiftThreshold = shiftAmt;
    }

    const num = parseInt(color, 16);
    let r = num >> 16;
    let g = (num >> 8) & 0x00FF;
    let b = num & 0x0000FF;

    if (r < shiftThreshold &&
        g < shiftThreshold &&
        b < shiftThreshold
    ) {
        r += shiftAmt;
        g += shiftAmt;
        b += shiftAmt;
    } else {
        r -= shiftAmt;
        g -= shiftAmt;
        b -= shiftAmt;
    }

    r = Math.max(Math.min(255, r), 0);
    g = Math.max(Math.min(255, g), 0);
    b = Math.max(Math.min(255, b), 0);

    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}

void main();

async function main() {
    // Canvas starts hidden via CSS (opacity: 0) to prevent flash of full-size content
    const $canvas = document.querySelector<HTMLElement>('#main-canvas');

    // Show loading spinner while canvas initializes
    const $canvasParent = $canvas?.parentElement;
    let $loadingOverlay: HTMLElement | null = null;
    if ($canvasParent) {
        $loadingOverlay = html.FromString(`
            <div class="pin-loading-overlay">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>
        `).firstElementChild as HTMLElement;
        $canvasParent.appendChild($loadingOverlay);
    }

    const engine = new CustomizerEngine({
        container: 'main-canvas',
        width: 1024,
        height: 1024,
        // Export at 1200 DPI for 1.25" pin width (1200 * 1.25 = 1500px)
        exportScale: 1500 / 1024,
    });

    const { colorLayers, textOnePathData, textOneAlternatePathData } = await setupEngine(engine);

    engine.fitStageIntoParentContainer();

    setupResponsive(engine);

    // Create shared color state for tracking manual edits
    const colorState = new ColorState();

    // Setup color pickers with shared state
    await setupRibbonColorPickers(colorLayers, colorState);

    // Setup preset manager
    const presetManager = new PresetManager({
        engine,
        colorState,
        presets,
        defaultFontSizes,
        shiftColor,
    });
    presetManager.initialize();

    // Text customizer handles top/bottom curved text
    const textCustomizer = new TextCustomizerUI(engine);

    // Switch text-one path based on whether text-two has content
    const $textTwoInput = document.querySelector<HTMLInputElement>('#text-two-input');
    const textOne = engine.getLayer('text-one') as RibbonTextLayer | undefined;
    if ($textTwoInput && textOneAlternatePathData && textOne) {
        const updateTextOnePath = () => {
            const hasLineTwoText = $textTwoInput.value.trim().length > 0;
            const usingAltPath = !hasLineTwoText;
            textOne.setPathData(usingAltPath ? textOneAlternatePathData : textOnePathData);
            // Notify text customizer so it can update bounds and refresh debug overlay
            textCustomizer.notifyTextOnePathChanged(usingAltPath);
        };
        $textTwoInput.addEventListener('input', updateTextOnePath);
        // Set initial state
        updateTextOnePath();
    }

    void setupUnitPriceDisplay();

    const $addToCartBtn = document.querySelector('#add-to-cart-btn');
    $addToCartBtn?.addEventListener('click', () => {
        void addToCart(engine);
    });

    // Setup mobile sticky bar
    setupMobileStickyBar();

    // iOS Safari mobile keyboard preview positioning
    // Android uses viewport meta tag with interactive-widget=resizes-content
    setupMobileKeyboardPreview(engine);

    // Automatically select the preset for the current month
    const currentMonthPreset = presets[new Date().getMonth()];
    if (currentMonthPreset) {
        presetManager.selectPreset(currentMonthPreset);
    }

    // The default self-hosted face must be applied before reveal, or the first
    // frames would show a device fallback font the print file couldn't match
    await textCustomizer.ready;

    // Reveal canvas and hide loading spinner after setup completes
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                $canvas?.style.setProperty('opacity', '1');
                if ($loadingOverlay) {
                    $loadingOverlay.style.opacity = '0';
                    setTimeout(() => $loadingOverlay?.remove(), 200);
                }
            }, 0);
        });
    });
}

interface colorLayerInfo {
    layer: ImageLayer;
    svgUrl: string;
}

interface engineSetupResult {
    colorLayers: Map<string, colorLayerInfo>;
    textOnePathData: string;
    textOneAlternatePathData: string;
}

async function setupEngine(engine: CustomizerEngine): Promise<engineSetupResult> {
    const imgPath = `${Relref('/')}img/custom-awareness-ribbon-pins`;
    const canvasCenter = { x: 512, y: 512 };
    const colorLayers = new Map<string, colorLayerInfo>();

    const bg = new ImageLayer('bg', {
        userEditable: false,
        isBackground: true,
    });
    await bg.setImage(`${imgPath}/background.png`);
    await engine.addLayer('bg', bg);

    // Add color layers (SVGs for colorable ribbon regions)
    // Scale: 1024/150, centered on canvas
    const svgColorLayerNames = ['color-1', 'color-2', 'color-3'];
    for (const name of svgColorLayerNames) {
        const svgUrl = `${imgPath}/${name}.svg`;
        const layer = new ImageLayer(name, {
            userEditable: false,
            isBackground: false,
        });

        // Fetch and cache the original SVG content
        const response = await fetch(svgUrl);
        const svgContent = await response.text();
        svgCache.set(name, svgContent);

        await layer.setImage(svgUrl);
        await engine.addLayer(name, layer);

        // SVG viewBox is 34.39x58, scale uniformly to cover 1024x1024 canvas
        const svgWidth = 34.39;
        const svgHeight = 58;
        const scale = Math.min(1024 / svgWidth, 1024 / svgHeight);
        layer.setScale(scale, scale);

        // Center the layer on the canvas
        // Since ImageLayer sets offset to image center, position is where the center goes
        layer.setPosition(canvasCenter.x, canvasCenter.y);

        colorLayers.set(name, { layer, svgUrl });
    }

    const svgTextPathNames = ['text-path-1', 'text-path-2', 'text-path-3', 'text-path-4'];
    for (const name of svgTextPathNames) {
        const svgUrl = `${imgPath}/${name}.svg`;

        // Fetch and cache the original SVG content
        const response = await fetch(svgUrl);
        const svgContent = await response.text();
        svgCache.set(name, svgContent);
    }

    // Extract path data for text-one (two variants: with and without line two)
    let textOnePathData = '';
    let textOneAlternatePathData = '';
    const parser = new DOMParser();

    if (svgCache.has('text-path-1')) {
        const svg = svgCache.get('text-path-1') || '';
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const pathEl = doc.querySelector('path.cls-1');
        if (pathEl) {
            textOnePathData = pathEl.getAttribute('d') || '';
        }
    }

    // Alternate path for text-one when line two is empty
    if (svgCache.has('text-path-4')) {
        const svg = svgCache.get('text-path-4') || '';
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const pathEl = doc.querySelector('path.cls-1');
        if (pathEl) {
            textOneAlternatePathData = pathEl.getAttribute('d') || '';
        }
    }

    const textOne = new RibbonTextLayer('text-one', {
        font: {
            family: DEFAULT_FONT_FAMILY,
            weight: 'bold',
            size: defaultFontSizes.textOne,
            color: '#ffffff',
        },
        pathData: textOnePathData,
        center: canvasCenter,
    });
    await engine.addLayer('text-one', textOne);

    let textTwoPathData = '';
    if (svgCache.has('text-path-2')) {
        const svg = svgCache.get('text-path-2') || '';
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const pathEl = doc.querySelector('path.cls-1');
        if (pathEl) {
            textTwoPathData = pathEl.getAttribute('d') || '';
        }
    }

    const textTwo = new RibbonTextLayer('text-two', {
        font: {
            family: DEFAULT_FONT_FAMILY,
            weight: 'bold',
            size: defaultFontSizes.textTwo,
            color: '#ffffff',
        },
        pathData: textTwoPathData,
        center: canvasCenter,
    });
    await engine.addLayer('text-two', textTwo);

    let textThreePathData = '';
    if (svgCache.has('text-path-3')) {
        const svg = svgCache.get('text-path-3') || '';
        const doc = parser.parseFromString(svg, 'image/svg+xml');
        const pathEl = doc.querySelector('path.cls-1');
        if (pathEl) {
            textThreePathData = pathEl.getAttribute('d') || '';
        }
    }

    const textThree = new RibbonTextLayer('text-three', {
        font: {
            family: DEFAULT_FONT_FAMILY,
            weight: 'bold',
            size: defaultFontSizes.textThree,
            color: '#ffffff',
        },
        pathData: textThreePathData,
        center: canvasCenter,
    });
    await engine.addLayer('text-three', textThree);

    // Fix layer ordering - layers with zIndex 0 get moved to bottom when added,
    // so we need to explicitly reorder them. Order from bottom to top:
    // bg -> color-1 -> color-2 -> color-3 -> text-one -> text-two -> text-three
    engine.getLayer('bg')?.sendToBack();
    engine.getLayer('color-1')?.bringToFront();
    engine.getLayer('color-2')?.bringToFront();
    engine.getLayer('color-3')?.bringToFront();
    engine.getLayer('text-one')?.bringToFront();
    engine.getLayer('text-two')?.bringToFront();
    engine.getLayer('text-three')?.bringToFront();

    return { colorLayers, textOnePathData, textOneAlternatePathData };
}

async function setupRibbonColorPickers(colorLayers: Map<string, colorLayerInfo>, colorState: ColorState) {
    const pickerConfigs = [
        { pickerId: 'ribbon-color-1-swatch', layerName: 'color-1' },
        { pickerId: 'ribbon-color-2-swatch', layerName: 'color-2' },
        { pickerId: 'ribbon-color-3-swatch', layerName: 'color-3' },
    ];

    // Fetch the ribbon preview SVG once (also the vector source for the EPS print file)
    const imgPath = `${Relref('/')}img/custom-awareness-ribbon-pins`;
    const svgUrl = `${imgPath}/ribbon-pieces.svg`;
    const response = await fetch(svgUrl);
    const svgContent = await response.text();
    svgCache.set('ribbon-pieces', svgContent);

    // Parse SVG and prepare for cloning
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgTemplate = svgDoc.querySelector('svg');

    // Get references to all pickers and displays
    const pickers = new Map<string, { $picker: HTMLInputElement; layerInfo: colorLayerInfo; $svg: SVGSVGElement | null }>();

    for (const { pickerId, layerName } of pickerConfigs) {
        const $picker = document.querySelector<HTMLInputElement>(`#${pickerId}`);
        const layerInfo = colorLayers.get(layerName);

        if (!$picker || !layerInfo) {
            continue;
        }

        // Clone and add SVG preview to the picker's parent label
        let $svg: SVGSVGElement | null = null;
        if (svgTemplate) {
            $svg = svgTemplate.cloneNode(true) as SVGSVGElement;
            $svg.classList.add('p-2');
            const $label = $picker.parentElement;
            if ($label) {
                $label.insertBefore($svg, $label.firstChild);
            }
        }

        pickers.set(layerName, { $picker, layerInfo, $svg });
    }

    // Helper to update a single SVG preview - only shows its own color, others are white
    // Uses inline styles to avoid CSS class name conflicts between SVGs
    const updatePreviewColor = (layerName: string, color: string) => {
        const picker = pickers.get(layerName);
        if (!picker?.$svg) {
            return;
        }
        // Set fill directly on path elements to avoid CSS class conflicts
        for (const colorClass of ['color-1', 'color-2', 'color-3']) {
            const paths = Array.from(picker.$svg.querySelectorAll(`.${colorClass}`));
            const fillColor = colorClass === layerName ? color : '#ffffff';
            for (const path of paths) {
                (path as SVGElement).style.fill = fillColor;
            }
        }
    };

    // Per-line text color picker references
    const $textOneColorPicker = document.querySelector<HTMLInputElement>('#color-text-one-input');
    const $textOneColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-one-input');
    const $textTwoColorPicker = document.querySelector<HTMLInputElement>('#color-text-two-input');
    const $textTwoColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-two-input');
    const $textThreeColorPicker = document.querySelector<HTMLInputElement>('#color-text-three-input');
    const $textThreeColorIndicator = document.querySelector<HTMLElement>('#color-indicator-text-three-input');

    // Mark text colors as manually set when user changes them (not programmatic changes)
    const setupTextColorManualTracking = ($picker: HTMLInputElement | null, lineId: string) => {
        if (!$picker) {
            return;
        }
        $picker.addEventListener('input', () => {
            if (!colorState.isTextColorChangeProgrammatic(lineId)) {
                colorState.markTextColorManuallySet(lineId);
            }
        });
    };

    setupTextColorManualTracking($textOneColorPicker, 'text-one');
    setupTextColorManualTracking($textTwoColorPicker, 'text-two');
    setupTextColorManualTracking($textThreeColorPicker, 'text-three');

    // Helper to update text color based on contrast with ribbon color
    // Prefers white, falls back to black if contrast is insufficient
    const updateTextColorForContrast = (
        ribbonColor: string,
        $picker: HTMLInputElement | null,
        $indicator: HTMLElement | null,
        lineId: string,
    ) => {
        if (colorState.isTextColorManuallySet(lineId) || !$picker) {
            return;
        }

        const whiteContrast = ContrastRatioHex(ribbonColor, '#ffffff');
        const newTextColor = whiteContrast !== null && whiteContrast >= 2 ? '#ffffff' : '#000000';

        if ($picker.value.toLowerCase() !== newTextColor) {
            $picker.value = newTextColor;
            if ($indicator) {
                $indicator.style.backgroundColor = newTextColor;
            }
            // Trigger input event so TextCustomizerUI picks up the change
            colorState.beginProgrammaticTextColorChange(lineId);
            $picker.dispatchEvent(new Event('input', { bubbles: true }));
            colorState.endProgrammaticTextColorChange(lineId);
        }
    };

    // Helper to update a color picker and its layer
    const updateColor = (layerName: string, color: string) => {
        const picker = pickers.get(layerName);
        if (!picker) {
            return;
        }
        picker.$picker.value = color;
        void updateSvgColor(picker.layerInfo, layerName, color);
        updatePreviewColor(layerName, color);
    };

    // Helper to get the current ribbon color for a layer
    const getRibbonColor = (layerName: string): string => {
        const picker = pickers.get(layerName);
        return picker?.$picker.value ?? '#ffffff';
    };

    // Update text contrast based on ribbon colors:
    // - text-one and text-two contrast against color-1
    // - text-three contrasts against color-2
    const updateAllTextContrasts = () => {
        const color1 = getRibbonColor('color-1');
        const color2 = getRibbonColor('color-2');
        updateTextColorForContrast(color1, $textOneColorPicker, $textOneColorIndicator, 'text-one');
        updateTextColorForContrast(color1, $textTwoColorPicker, $textTwoColorIndicator, 'text-two');
        updateTextColorForContrast(color2, $textThreeColorPicker, $textThreeColorIndicator, 'text-three');
    };

    // Set up event listeners
    for (const [layerName, { $picker, layerInfo }] of pickers) {
        $picker.addEventListener('input', () => {
            const newColor = $picker.value;
            void updateSvgColor(layerInfo, layerName, newColor);

            if (layerName === 'color-1') {
                // Color-1 changes propagate to color-2 and color-3 if they haven't been manually set
                if (!colorState.isColorManuallySet('color-2')) {
                    const color2 = shiftColor(newColor, false);
                    updateColor('color-2', color2);
                    // Also update text-three contrast since it checks against color-2
                    updateTextColorForContrast(color2, $textThreeColorPicker, $textThreeColorIndicator, 'text-three');
                }
                if (!colorState.isColorManuallySet('color-3')) {
                    updateColor('color-3', shiftColor(newColor, true));
                }
                // Update text-one and text-two contrast (they check against color-1)
                updateTextColorForContrast(newColor, $textOneColorPicker, $textOneColorIndicator, 'text-one');
                updateTextColorForContrast(newColor, $textTwoColorPicker, $textTwoColorIndicator, 'text-two');
            } else if (layerName === 'color-2') {
                // Mark color-2 as manually set (only if not programmatic) and update text-three contrast
                if (!colorState.isProgrammaticColorChange) {
                    colorState.markColorManuallySet(layerName);
                }
                updateTextColorForContrast(newColor, $textThreeColorPicker, $textThreeColorIndicator, 'text-three');
            } else {
                // Mark color-3 as manually set (only if not programmatic, no text contrasts against color-3)
                if (!colorState.isProgrammaticColorChange) {
                    colorState.markColorManuallySet(layerName);
                }
            }

            // Update this picker's SVG preview
            updatePreviewColor(layerName, newColor);
        });

        // Initialize picker value (but don't update SVG yet - wait for sync below)
        $picker.value = $picker.value || '#ff0000';
    }

    // On initial load, sync color-2 and color-3 to color-1, then update all SVG colors
    // This avoids a race condition where updateSvgColor could be called twice for color-2/3
    const color1Picker = pickers.get('color-1');
    if (color1Picker) {
        const initialColor = color1Picker.$picker.value;
        const color2 = shiftColor(initialColor, false);
        const color3 = shiftColor(initialColor, true);

        // Set picker values for color-2 and color-3
        const color2Picker = pickers.get('color-2');
        const color3Picker = pickers.get('color-3');
        if (color2Picker) {
            color2Picker.$picker.value = color2;
        }
        if (color3Picker) {
            color3Picker.$picker.value = color3;
        }

        // Now update all SVG colors and previews (only once per layer)
        for (const [layerName, { layerInfo, $picker }] of pickers) {
            void updateSvgColor(layerInfo, layerName, $picker.value);
            updatePreviewColor(layerName, $picker.value);
        }

        updateAllTextContrasts();
    }
}

async function updateSvgColor(layerInfo: colorLayerInfo, layerName: string, newColor: string) {
    const originalSvg = svgCache.get(layerName);
    if (!originalSvg) {
        return;
    }

    // Replace fill color in the SVG
    // The visible path has a fill="..." attribute, replace it with the new color
    const modifiedSvg = originalSvg.replace(/fill="#[0-9a-fA-F]{6}"/g, `fill="${newColor}"`);

    // Convert to data URL
    const dataUrl = 'data:image/svg+xml;base64,' + btoa(modifiedSvg);

    // Update the layer with the new image
    await layerInfo.layer.setImage(dataUrl);

    // Reapply scale and position
    // SVG viewBox is 34.39x58, scale uniformly to cover 1024x1024 canvas
    // Using the smaller ratio ensures the ribbon is fully visible
    const svgWidth = 34.39;
    const svgHeight = 58;
    const scale = Math.min(1024 / svgWidth, 1024 / svgHeight);
    const canvasCenter = { x: 512, y: 512 };
    layerInfo.layer.setScale(scale, scale);
    layerInfo.layer.setPosition(canvasCenter.x, canvasCenter.y);
}

function setupResponsive(engine: CustomizerEngine) {
    window.addEventListener('resize', () => {
        engine.fitStageIntoParentContainer();
    });
}

/**
 * iOS Safari Mobile Keyboard Preview Handler
 *
 * When a text input is focused, show a simple modal with:
 * - Preview at top
 * - Single text input
 * - Cancel / Done buttons
 */
function setupMobileKeyboardPreview(engine: CustomizerEngine) {
    // Only run on mobile devices
    if (!Mobile()) {
        return;
    }

    if (!window.visualViewport) {
        return;
    }

    const viewport = window.visualViewport;
    let $backdrop: HTMLDivElement | null = null;
    let $overlay: HTMLDivElement | null = null;
    let $overlayInput: HTMLInputElement | null = null;
    let $originalInput: HTMLInputElement | null = null;
    let originalValue = '';
    let savedScrollY = 0;

    const $previewCard = document.querySelector<HTMLElement>('#preview-card');
    const $previewSection = document.querySelector<HTMLElement>('#pin-preview-section');
    const $previewCardParent = $previewCard?.parentElement;
    const $previewCardNext = $previewCard?.nextSibling;
    let previewCardOriginalStyle = '';
    let previewSectionOriginalStyle = '';

    const textInputs = document.querySelectorAll<HTMLInputElement>(
        '#text-one-input, #text-two-input, #text-three-input',
    );

    // Prevent all touch scrolling when overlay is open
    const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
    };

    const openInputModal = ($input: HTMLInputElement) => {
        if ($overlay) {
            return;
        }

        $originalInput = $input;
        originalValue = $input.value;

        // Get the label for this input
        const $label = $input.closest('.pin-text-question-container')?.querySelector('span');
        const labelText = $label?.textContent || 'Text';

        // Lock body scroll
        savedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';

        // Block touch scrolling
        document.addEventListener('touchmove', handleTouchMove, { passive: false });

        // Create white backdrop to cover body (visible through translucent iOS keyboard)
        $backdrop = document.createElement('div');
        $backdrop.id = 'keyboard-backdrop';
        $backdrop.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: white;
            z-index: 1059;
        `;
        document.body.appendChild($backdrop);

        // Calculate sizes - preview takes 50% of visible viewport when keyboard is open
        const previewHeight = viewport.height * 0.5;

        // Create overlay
        $overlay = document.createElement('div');
        $overlay.id = 'keyboard-overlay';
        $overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: ${viewport.height}px;
            z-index: 1060;
            display: flex;
            flex-direction: column;
            background: white;
            transform: translateY(${viewport.offsetTop}px);
        `;

        // Move actual preview card (not clone) so canvas keeps working
        if ($previewCard && $previewSection) {
            previewCardOriginalStyle = $previewCard.style.cssText;
            previewSectionOriginalStyle = $previewSection.style.cssText;

            $previewCard.style.cssText = `
                flex: 0 0 ${previewHeight}px;
                margin: 0;
                border-radius: 0;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                overflow: visible;
            `;
            $previewSection.style.cssText = `
                height: 100%;
                min-height: 0;
                padding: 0.5rem;
                overflow: visible;
            `;
            $overlay.appendChild($previewCard);
        }

        // Create input area
        const $inputArea = document.createElement('div');
        $inputArea.style.cssText = `
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            padding: 1rem;
            gap: 1rem;
        `;

        // Label
        const $labelEl = document.createElement('label');
        $labelEl.textContent = labelText;
        $labelEl.style.cssText = 'font-weight: 600; font-size: 1rem;';
        $inputArea.appendChild($labelEl);

        // Input
        $overlayInput = document.createElement('input');
        $overlayInput.type = 'text';
        $overlayInput.value = $input.value;
        $overlayInput.placeholder = $input.placeholder;
        $overlayInput.className = 'form-control';
        $inputArea.appendChild($overlayInput);

        // Sync input changes to original (updates preview in real-time)
        $overlayInput.addEventListener('input', () => {
            if ($originalInput && $overlayInput) {
                $originalInput.value = $overlayInput.value;
                $originalInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        });

        // Buttons
        const $buttons = document.createElement('div');
        $buttons.style.cssText = 'display: flex; gap: 0.5rem; margin-top: auto;';

        const $cancelBtn = document.createElement('button');
        $cancelBtn.type = 'button';
        $cancelBtn.className = 'btn btn-outline-secondary flex-grow-1';
        $cancelBtn.textContent = 'Cancel';
        $cancelBtn.addEventListener('click', () => {
            // Restore original value
            if ($originalInput) {
                $originalInput.value = originalValue;
                $originalInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
            closeInputModal();
        });

        const $doneBtn = document.createElement('button');
        $doneBtn.type = 'button';
        $doneBtn.className = 'btn btn-secondary flex-grow-1';
        $doneBtn.textContent = 'Done';
        $doneBtn.addEventListener('click', () => {
            closeInputModal();
        });

        $buttons.appendChild($cancelBtn);
        $buttons.appendChild($doneBtn);
        $inputArea.appendChild($buttons);

        $overlay.appendChild($inputArea);
        document.body.appendChild($overlay);

        // Focus must happen synchronously during user gesture on iOS
        $overlayInput.focus();

        // Update sizes once keyboard is open
        updateOverlayPosition();
    };

    const closeInputModal = () => {
        if (!$overlay) {
            return;
        }

        // Blur to close keyboard
        $overlayInput?.blur();

        // Remove touch handler
        document.removeEventListener('touchmove', handleTouchMove);

        // Unlock body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';

        // Move preview card back
        if ($previewCard && $previewCardParent) {
            $previewCard.style.cssText = previewCardOriginalStyle;
            if ($previewSection) {
                $previewSection.style.cssText = previewSectionOriginalStyle;
                const $previewContainer = $previewSection.querySelector('#pin-preview-container') as HTMLElement;
                if ($previewContainer) {
                    $previewContainer.style.cssText = '';
                }
            }
            if ($previewCardNext) {
                $previewCardParent.insertBefore($previewCard, $previewCardNext);
            } else {
                $previewCardParent.appendChild($previewCard);
            }
        }

        // Remove overlay and backdrop
        $overlay.remove();
        $overlay = null;
        $backdrop?.remove();
        $backdrop = null;
        $overlayInput = null;
        $originalInput = null;

        // Restore scroll position
        window.scrollTo(0, savedScrollY);

        // Resize canvas back to normal
        engine.fitStageIntoParentContainer();
    };

    const updateOverlayPosition = () => {
        if ($overlay && $previewCard && $previewSection) {
            const visibleHeight = viewport.height;
            $overlay.style.height = `${visibleHeight}px`;
            $overlay.style.transform = `translateY(${viewport.offsetTop}px)`;

            // Update preview to 50% of visible area above keyboard
            const previewHeight = Math.floor(visibleHeight * 0.5);
            $previewCard.style.height = `${previewHeight}px`;
            $previewCard.style.flex = 'none';
            $previewSection.style.height = '100%';

            // Make preview container fit within available height
            const $previewContainer = $previewSection.querySelector('#pin-preview-container') as HTMLElement;
            if ($previewContainer) {
                $previewContainer.style.cssText = `
                    max-height: 100% !important;
                    max-width: 100% !important;
                    height: 100% !important;
                    width: auto !important;
                `;
            }

            // Resize canvas to fit new container
            engine.fitStageIntoParentContainer();
        }
    };

    // Listen for focus on text inputs - intercept and show modal instead
    textInputs.forEach($input => {
        $input.addEventListener('focus', () => {
            // Open modal immediately (focus must stay in user gesture chain)
            openInputModal($input);
        });
    });

    viewport.addEventListener('resize', updateOverlayPosition);
    viewport.addEventListener('scroll', updateOverlayPosition);
}

function setupMobileStickyBar() {
    const $mobileQuantity = document.querySelector<HTMLInputElement>('#mobile-quantity');
    const $mobilePrice = document.querySelector<HTMLElement>('#mobile-unit-price');
    const $mobileAddToCart = document.querySelector<HTMLElement>('#mobile-add-to-cart-btn');
    const $quantity = document.querySelector<HTMLInputElement>('#quantity');
    const $unitPrice = document.querySelector<HTMLElement>('#unit-price');
    const $addToCartBtn = document.querySelector<HTMLElement>('#add-to-cart-btn');

    if (!$mobileQuantity || !$mobilePrice || !$mobileAddToCart) {
        return;
    }

    // Sync quantity between main and mobile inputs
    if ($quantity) {
        $mobileQuantity.value = $quantity.value;

        $quantity.addEventListener('input', () => {
            $mobileQuantity.value = $quantity.value;
        });

        $mobileQuantity.addEventListener('input', () => {
            $quantity.value = $mobileQuantity.value;
            $quantity.dispatchEvent(new Event('input', { bubbles: true }));
        });
    }

    // Sync price display
    if ($unitPrice) {
        $mobilePrice.textContent = $unitPrice.textContent || '$XX.XX';

        const priceObserver = new MutationObserver(() => {
            $mobilePrice.textContent = $unitPrice.textContent || '$XX.XX';
        });
        priceObserver.observe($unitPrice, { childList: true, characterData: true, subtree: true });
    }

    // Mobile add-to-cart button triggers main button
    $mobileAddToCart.addEventListener('click', () => {
        $addToCartBtn?.click();
    });
}

async function setupUnitPriceDisplay() {
    const $quantity = document.querySelector('#quantity') as HTMLInputElement | null;
    const $unitPrice = document.querySelector('#unit-price') as HTMLElement | null;
    const $unitPriceDiscount = document.querySelector('#unit-price-discount') as HTMLElement | null;
    const $unitPriceDiscountPercent = document.querySelector('#unit-price-discount-percent') as HTMLElement | null;

    if (!$quantity || !$unitPrice || !$unitPriceDiscount || !$unitPriceDiscountPercent) {
        return;
    }

    const maxUnitPrice = await GetPrice(
        'lapel pin',
        'Made in USA - Rush Pins',
        'Ribbon',
        1,
    );

    const updatePrice = async () => {
        const quantity = parseInt($quantity.value, 10) || 1;
        const unitPrice = await GetPrice(
            'lapel pin',
            'Made in USA - Rush Pins',
            'Ribbon',
            quantity,
        );
        $unitPrice.textContent = DollarFormat(unitPrice);

        const discount = maxUnitPrice.sub(unitPrice);
        if (discount.gt(0)) {
            const discountPercent = Math.round(discount.div(maxUnitPrice).toNumber() * 100);
            $unitPriceDiscountPercent.textContent = discountPercent.toString();
            $unitPriceDiscount.classList.remove('d-none');
        } else {
            $unitPriceDiscount.classList.add('d-none');
        }
    };

    $quantity.addEventListener('input', () => {
        void updatePrice();
    });

    void updatePrice();
}

// Gather the current design state and hand it to the vector EPS exporter.
// The exporter module (and opentype.js behind it) only loads at add-to-cart.
async function buildPrintEps(engine: CustomizerEngine): Promise<Blob | null> {
    const piecesSvg = svgCache.get('ribbon-pieces');
    if (!piecesSvg) {
        return null;
    }

    const ribbonColors: Record<string, string> = {};
    for (const name of ['color-1', 'color-2', 'color-3']) {
        const $picker = document.querySelector<HTMLInputElement>(`#ribbon-${name}-swatch`);
        if (!$picker?.value) {
            return null;
        }
        ribbonColors[name] = $picker.value;
    }

    const textLayers = ['text-one', 'text-two', 'text-three'].
        map(name => engine.getLayer(name)).
        filter((layer): layer is RibbonTextLayer => layer instanceof RibbonTextLayer);

    const epsExport = await import('../../custom-awareness-ribbon-pins/eps-export');
    return epsExport.BuildRibbonPinEps({
        piecesSvg,
        ribbonColors,
        textLayers,
    });
}

async function addToCart(engine: CustomizerEngine) {
    const $addToCartBtn = document.querySelector<HTMLButtonElement>('#add-to-cart-btn');
    const $quantity = document.querySelector<HTMLInputElement>('#quantity');
    const $canvas = document.querySelector<HTMLElement>('#main-canvas');
    const $canvasParent = $canvas?.parentElement;

    if (!$addToCartBtn || !$quantity) {
        return;
    }

    buttons.Start($addToCartBtn);
    $addToCartBtn.disabled = true;

    // Ribbon pins have no uploaded artwork — the typed text lines are the only text
    // source, so spell-check them directly (exact, no OCR) before the expensive exports.
    const $textOneInput = document.querySelector<HTMLInputElement>('#text-one-input');
    const $textTwoInput = document.querySelector<HTMLInputElement>('#text-two-input');
    const $textThreeInput = document.querySelector<HTMLInputElement>('#text-three-input');
    const pinText = [$textOneInput?.value, $textTwoInput?.value, $textThreeInput?.value].
        map(v => v?.trim()).filter(Boolean).join('\n');

    let findings: models.SpellCheckFinding[] = [];
    if (pinText) {
        try {
            const resp = await SpellCheck([{ text: pinText, uploadIDs: [] }]);
            findings = resp.findings;
        } catch {
            // Fail open — spell-check is best-effort; don't block checkout on an outage.
            findings = [];
        }
    }
    if (findings.length > 0) {
        const confirmed = await ConfirmSpellCheck(findings);
        if (!confirmed) {
            buttons.Stop($addToCartBtn);
            $addToCartBtn.disabled = false;
            return;
        }
    }

    // Show loading overlay and hide canvas during export to prevent background blink
    let $exportOverlay: HTMLElement | null = null;
    if ($canvasParent && $canvas) {
        $exportOverlay = html.FromString(`
            <div class="pin-loading-overlay">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Preparing...</span>
                </div>
            </div>
        `).firstElementChild as HTMLElement;
        $canvasParent.appendChild($exportOverlay);
        $canvas.style.visibility = 'hidden';
    }

    try {
        const options: { name: string; value: string }[] = [];
        if ($textOneInput?.value) {
            options.push({ name: 'Line 1', value: $textOneInput.value });
        }
        if ($textTwoInput?.value) {
            options.push({ name: 'Line 2', value: $textTwoInput.value });
        }
        if ($textThreeInput?.value) {
            options.push({ name: 'Line 3', value: $textThreeInput.value });
        }

        // Export layers (background toggles visibility during these calls)
        const whiteLayer = await engine.exportWhiteLayer();
        const colorLayer = await engine.exportColorLayer();

        // Experimental vector print file (issue #4245) — never blocks checkout;
        // null means the design isn't vectorizable (unsupported font, emoji)
        let epsBlob: Blob | null = null;
        try {
            epsBlob = await buildPrintEps(engine);
        } catch (err) {
            console.error('EPS export failed', err);
        }

        // Upload preview image first
        const previewDataURL = engine.exportAsDataURL('image/png', undefined, 2048);
        const previewUpload = (await uploads.NewFromURL([{
            name: 'preview.png',
            mime: 'image/png',
            url: previewDataURL,
        }]))[0];

        // Upload color and white layers
        const stageUploads = await uploads.NewFromURL([
            {
                name: '.front.jpg',
                mime: 'image/jpeg',
                url: colorLayer,
            },
            {
                name: '.front-white.jpg',
                mime: 'image/jpeg',
                url: whiteLayer,
            },
        ]);

        if (epsBlob) {
            stageUploads.push(...await uploads.New('.front.eps', 'application/postscript', epsBlob));
        }

        const params: AddCartItemParamsUpload = {
            uploadID: previewUpload.uploadID,
            quantity: parseInt($quantity.value, 10) || 1,
            options: options,
            cartItemUploadIDs: stageUploads.map(u => u.uploadID),
            cartItemUploads: [
                {
                    uploadID: previewUpload.uploadID,
                    tags: ['proof'],
                },
            ],
            uploadLineItem: {
                name: 'Custom Awareness Ribbon Pins',
                lineItem: 'lapel pin',
                type: 'Made in USA - Rush Pins',
                size: 'Ribbon',
                networkSKUID: models.NetworkSKUID.NETWORK_SK_US_RIBBON_PINS,
                childLineItems: [],
            },
        };

        await AddCartItems(params);
        window.location.href = Relref('/cart');
    } catch (err) {
        // Remove overlay and restore canvas visibility on error
        $exportOverlay?.remove();
        if ($canvas) {
            $canvas.style.visibility = '';
        }
        buttons.Stop($addToCartBtn);
        $addToCartBtn.disabled = false;
        ShowError(err);
    }
}